/**
 * shareStatus — one vocabulary for the GAS-share gate (boundary decision #8).
 *
 * The list badge, the detail status bar and the toolbar filter all read from
 * here so they can never drift into three different words for one state.
 *
 * Plain language beats the schema word: the stored statuses are
 * pending/approved/excluded, but nobody reading the screen should have to
 * learn that. What they need to know is whether the executive summary is
 * visible in the in-tenant GAS dashboard.
 */

import { Cloud, CloudOff, CircleDashed } from 'lucide-react';

export const SHARE_STATES = {
  approved: {
    key: 'approved',
    Icon: Cloud,
    badge: 'In GAS',
    title: 'Shared to GAS',
    // Detail-pane sentence. GAS caches the summaries feed for 5 minutes, so
    // "shared" is not the same as "visible right now" — say so rather than
    // letting it look broken for the first few minutes.
    hint: 'The executive summary and action items appear in the GAS Meeting Notes tab. GAS refreshes every 5 minutes.',
    listHint: 'Executive summary is shared to the GAS dashboard',
    tone: 'ok',
  },
  pending: {
    key: 'pending',
    Icon: CircleDashed,
    badge: 'Not shared',
    title: 'Not shared yet',
    hint: 'Invisible in GAS until you share it. Only the summary and action items ever cross — never the transcript.',
    listHint: 'Not shared to GAS yet — needs a decision',
    tone: 'warn',
  },
  excluded: {
    key: 'excluded',
    Icon: CloudOff,
    badge: 'Private',
    title: 'Kept private',
    hint: 'Stays in TekStak. Nothing from this recording reaches the GAS dashboard.',
    listHint: 'Kept private — never shown in GAS',
    tone: 'muted',
  },
};

/** Normalize a raw DB value (may be null on older rows) to a known state. */
export function shareStateOf(recording) {
  return SHARE_STATES[recording?.tenant_share_status] || SHARE_STATES.pending;
}

/**
 * Personal recordings are not GAS candidates at all — the tenant boundary
 * exists to keep them out. Showing a "share to GAS" button on a personal
 * recording invites exactly the mistake the gate is there to prevent.
 */
export function isGasCandidate(recording) {
  // categoryInfo is the enriched view of the same stored field; read it first
  // so an optimistic category change flips this in the same render.
  return (recording?.categoryInfo?.category ?? recording?.category) === 'work';
}
